<script setup lang="ts">
import AppTabs from '@/shared/ui/AppTabs/index';
import AppSelect from '@/shared/ui/AppSelect/index';
import ColumnsBlock from '@/shared/ui/ColumnsBlock/index';
import { ROOT_CATALOG_URL } from '@/shared/constants/urlConstants';
import { useFindLawyer } from '@/pages/main/composables/useFindLawyer';

const isMobile: boolean = useIsMobile();

const {
  tabList,
  activeTab,
  categoryColumnsItems,
  cityColumnsItems,
  filteredCategoryItems,
  filteredCityItems,
  searchCategories,
  searchCities,
} = useFindLawyer();
</script>

<template>
  <section :class="['find-lawyer', { _mobile: isMobile }]" data-testid="find-lawyer">
    <h2 class="find-lawyer__title">
      <NuxtLink
        class="find-lawyer__link"
        :to="ROOT_CATALOG_URL"
        data-testid="find-lawyer-title-link"
      >
        Найти юриста
        <AppIcon class="find-lawyer__icon" name="ArrowRightMain" />
      </NuxtLink>
    </h2>
    <div class="find-lawyer__tabs">
      <AppTabs
        v-model="activeTab"
        :tab-list="tabList"
        :max-width="isMobile ? 'none' : '405px'"
      >
        <template #tabPanel-1>
          <div class="find-lawyer__tabs-content" data-testid="find-lawyer-categories">
            <template v-if="isMobile">
              <AppSelect
                :items="filteredCategoryItems"
                placeholder="Выберите специализацию"
                option-value-key="id"
                option-label-key="name"
                current-option-label-key="name"
                is-option-link
                with-input
                @input="searchCategories"
              />
            </template>
            <ColumnsBlock
              v-else
              :items="categoryColumnsItems"
              :columns-count="3"
              :count-in-column="10"
              :gap="20"
              key-name="specializations"
            >
              <template #default="{ item }">
                <NuxtLink :to="item?.link" class="columns-block__item-link">
                  <AppIcon
                    v-if="item?.iconName"
                    class="columns-block__item-icon"
                    directory="specializations"
                    :name="String(item.iconName)"
                  />
                  {{ item?.name }}
                </NuxtLink>
              </template>
            </ColumnsBlock>
          </div>
        </template>
        <template #tabPanel-2>
          <div class="find-lawyer__tabs-content" data-testid="find-lawyer-cities">
            <template v-if="isMobile">
              <AppSelect
                :items="filteredCityItems"
                placeholder="Выберите город"
                option-value-key="id"
                option-label-key="name"
                current-option-label-key="name"
                is-option-link
                with-input
                @input="searchCities"
              />
            </template>
            <ColumnsBlock
              v-else
              :items="cityColumnsItems"
              :columns-count="4"
              :count-in-column="15"
              :gap="72"
              key-name="cities"
            />
          </div>
        </template>
      </AppTabs>
    </div>
  </section>
</template>

<style lang="less">
.find-lawyer {
  .container-d();

  padding-bottom: 150px;

  &__title {
    .h-offset-reset();
    .title-section();

    display: flex;
    align-items: baseline;
    gap: @size-x5;
    margin-bottom: @size-x10;
  }

  &__link {
    .color-change-hover();

    display: inline-flex;
    align-items: baseline;
    gap: @size-x3;
  }

  &__icon {
    width: @size-x4;
    height: @size-x7;
  }

  &__tabs-content {
    width: 100%;
    padding: 41px 45px 46px 40px;
    box-shadow:
      -4px -5px 25px rgba(0, 0, 0, 0.06),
      0px @size-x10 @size-x10 -18px rgba(139, 144, 164, 0.2);
    border-radius: 0px @main-size @main-size @main-size;
  }

  .columns-block__item-link {
    transition: color @A ease-in-out;
  }

  .columns-block__item-icon path {
    transition: fill @A ease-in-out;
  }

  .columns-block__column {
    row-gap: @size-x4;
  }

  &._mobile {
    .container-m();

    padding-bottom: 72px;
  }

  &._mobile &__title {
    font-size: @size-x6;
    font-weight: 700;
    line-height: 25px;
    margin-bottom: @size-x5;
  }

  &._mobile &__link._mobile {
    align-items: center;
    gap: @size-x2;
    font-size: @size-x3;
    font-weight: 600;
    line-height: 130%;
    color: @color-persimmon;
    margin-left: auto;
  }

  &._mobile &__icon {
    width: @size-x2;
    height: @size-x4;

    &._mobile {
      width: 5px;
      height: @size-x2;

      path {
        stroke: @color-persimmon;
      }
    }
  }

  &._mobile &__tabs-content {
    .flex-column(@size-x5);

    padding: @size-x9 22px;
  }

  &._mobile &__tabs .app-tabs__list {
    overflow: visible;
  }

  &._mobile &__tabs .app-tabs__item {
    flex: 1 1 0;
  }
}
</style>
